import { useEffect, useRef, useState } from "react";

function isScrollableElement(el: HTMLElement) {
  const style = getComputedStyle(el);
  return (
    ["auto", "scroll"].includes(style.overflowY) ||
    ["auto", "scroll"].includes(style.overflow)
  );
}

export default function useIsScrollable<T extends HTMLElement>(
  deps: unknown[] = [],
) {
  const ref = useRef<T>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    const content = ref.current;
    if (!content) return;

    let scrollContainer: HTMLElement | null = null;

    const findScrollContainer = () => {
      let el: HTMLElement | null = content.parentElement;
      while (el) {
        if (isScrollableElement(el)) return el;
        el = el.parentElement;
      }
      return null;
    };

    const check = () => {
      scrollContainer = findScrollContainer();
      if (!scrollContainer) {
        setIsScrollable(false);
        return;
      }

      const hasOverflow =
        scrollContainer.scrollHeight > scrollContainer.clientHeight ||
        scrollContainer.scrollTop > 0;

      setIsScrollable(hasOverflow);
    };

    // Run after layout
    requestAnimationFrame(check);
    setTimeout(check, 0); // extra safety for modals

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(check);
    });

    observer.observe(content);

    const attachScrollListener = () => {
      const container = findScrollContainer();
      if (!container) return;
      container.addEventListener("scroll", check);
      return container;
    };

    const scrollEl = attachScrollListener();
    window.addEventListener("resize", check);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", check);
      if (scrollEl) scrollEl.removeEventListener("scroll", check);
    };
  }, deps);

  return { isScrollable, ref } as const;
}
